import { h } from 'preact'
import clsx from 'clsx'

interface PromolineData {
  title: string
  body?: string
  cta?: {
    text?: string
    url?: string
  }
  text_colour: string
  background_colour?: string
  priority: number
  end_date?: string
  countdown?: string[]
}

interface PromolineProps {
  promoline: PromolineData
  promolineLocation?: string
  promolineSize?: 'small' | 'medium' | 'large'
  title?: string
  body?: string
  cta_text?: string
  cta_url?: string
  text_colour?: string
  bg_colour?: string
  end_date?: string
  countdown?: string[]
}

export const Promoline = ({
  promoline,
  promolineLocation,
  title: titleOverride,
  body: bodyOverride,
  cta_text: ctaTextOverride,
  cta_url: ctaUrlOverride,
  text_colour: textColourOverride,
  bg_colour: bgColourOverride,
  end_date: endDateOverride,
  countdown: countdownOverride,
  className
}: PromolineProps & { className?: string }) => {
  if (!promoline || !promoline.title) return null

  const title = titleOverride ?? promoline.title
  const body = bodyOverride ?? promoline.body
  const ctaText = ctaTextOverride ?? promoline.cta?.text
  const ctaUrl = ctaUrlOverride ?? promoline.cta?.url
  const textColour = textColourOverride ?? promoline.text_colour
  const bgColour = bgColourOverride ?? promoline.background_colour
  const endDate = endDateOverride ?? promoline.end_date
  const countdown = countdownOverride ?? promoline.countdown

  const countdownString = Array.isArray(countdown) ? countdown.join(',') : ''

  const shouldShowCountdown = (() => {
    if (!endDate || !countdownString || !promolineLocation) return false
    return countdownString.includes(promolineLocation)
  })()

  const getConfig = () => {
    switch (promolineLocation) {
      case 'PDP':
        return {
          container: 'w-full',
          bgPadding: 'pt-0.125 pb-0.25 px-0.5',
          titleClass: 'paragraph-xs font-medium',
          countdownLayout: 'medium',
          countdownPosition: 'block'
        }
      case 'Card':
        return {
          container: 'w-fit',
          bgPadding: 'pt-0.125 pb-0.25 px-0.5 text-balance',
          titleClass: 'paragraph-xs font-medium ',
          countdownLayout: 'small',
          countdownPosition: 'inline'
        }
      default: // PLP
        return {
          container: 'w-fit',
          bgPadding: 'pt-0.125 pb-0.25 px-0.5 text-balance',
          titleClass: 'paragraph-xs font-medium',
          countdownLayout: 'small',
          countdownPosition: 'inline'
        }
    }
  }

  const sizeConfig = getConfig()
  const Component = ctaUrl ? 'a' : 'div'

  const componentProps = ctaUrl ? {
    href: ctaUrl,
    'aria-label': title
  } : {}

  return (
    <Component
      {...componentProps}
      className={clsx(
        'shrink-0 flex',
        !shouldShowCountdown && 'flex-wrap',
        'items-center justify-between gap-x-0.5 gap-y-0.25 rounded-[4px]',
        sizeConfig.container,
        bgColour && 'bg-[--bg-colour]',
        bgColour && sizeConfig.bgPadding,
        'h-fit text-[--text-colour]',
        ctaUrl && 'link-fade',
        className
      )}
      style={{
        '--text-colour': textColour,
        '--bg-colour': bgColour || 'transparent'
      } as React.CSSProperties}
    >
      <div className={clsx(
        'grow flex gap-x-0.5 gap-y-0.25',
        sizeConfig.container,
        shouldShowCountdown && sizeConfig.countdownPosition === 'block' ? 'flex-col' : 'items-center'
      )}>
        {sizeConfig.countdownPosition === 'inline' ? (
          <div className={clsx(
            'flex items-center',
            'flex-wrap'
          )}>
            <span className={sizeConfig.titleClass}>
              {title}{shouldShowCountdown ? '\u00A0|\u00A0' : ''}
            </span>

          </div>
        ) : (
          <>
            {(title || body) && (
              <div className="grow flex flex-col gap-y-0.25">
                {title && (
                  <span className={sizeConfig.titleClass}>
                    {title}!
                  </span>
                )}
                {body && (
                  <span className="grow paragraph-xs link-fade">
                    {body}
                  </span>
                )}
              </div>
            )}
          </>
        )}

        {ctaText && promolineLocation === 'PDP' && (
          <span className="shrink-0 size-fit paragraph-xs">
            {ctaText}
          </span>
        )}
      </div>
    </Component>
  )
}
